/* ══════════════════════════════════════════════════════════════════════════
   THE DISMISS ARBITER — one Escape stack for the whole app.

   Promoted verbatim from md-database.jsx (PP-4), where it governed only the
   database family while the rest of the app rode md-theme's useEscape — a
   mount-order lottery where every mounted instance fired. One key, four
   dispatchers, no arbiter: that is how Escape could close a surface that was
   not visually on top. Now there is one stack, and it IS the order things
   opened.

   The law:
     · A surface that can be dismissed registers a layer while it stands
       (useMdDismissLayer). Escape closes the topmost layer, only.
     · A field being edited keeps its own Escape semantics: with a layer
       standing, the first press only blurs the field (step one of two) —
       unless the topmost layer OWNS that field (a search field that exists
       only while its surface does is the surface; one press must close it,
       not merely leave it).
     · With no layer standing, the key passes through untouched.

   Registered at MODULE LOAD so it is queued on window-capture ahead of every
   listener a component mounts later, and stopImmediatePropagation silences
   the ones behind it. */
const mdDismissLayers = [];
window.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !mdDismissLayers.length) return;
  const target = event.target;
  const inField = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
  const top = mdDismissLayers[mdDismissLayers.length - 1];
  if (inField && !(target.closest && target.closest('.md-db-menu')) && !(top.owns && top.owns(target))) {
    event.preventDefault(); event.stopImmediatePropagation();
    target.blur();
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  mdDismissLayers[mdDismissLayers.length - 1]();
}, true);

function useMdDismissLayer(onClose, active = true, owns = null, surface = null) {
  const ref = React.useRef(onClose); ref.current = onClose;
  const ownsRef = React.useRef(owns); ownsRef.current = owns;
  React.useEffect(() => {
    if (!active) return;
    const layer = () => { if (ref.current) ref.current(); };
    layer.owns = (target) => !!(ownsRef.current && ownsRef.current(target));
    mdDismissLayers.push(layer);
    /* THE RIDE (N-9). A layer that names a `surface` — a registry id from
       md-scene — is announced to the App, which seats it on the one stack:
       it gets a journal entry, so browser Back dismisses it exactly like
       Escape, instead of tearing down whatever stands beneath. The token
       carries the layer's own close, because when a walk retires the entry
       only the owner knows how to leave. Layers without a surface keep
       today's behavior: Escape-only, no history entry. */
    const token = surface ? { surface, close: () => { if (ref.current) ref.current(); } } : null;
    if (token) window.dispatchEvent(new CustomEvent('mdsceneride', { detail: { token, open: true } }));
    return () => {
      const at = mdDismissLayers.lastIndexOf(layer); if (at >= 0) mdDismissLayers.splice(at, 1);
      if (token) window.dispatchEvent(new CustomEvent('mdsceneride', { detail: { token, open: false } }));
    };
  }, [active, surface]);
}

/* ══════════════════════════════════════════════════════════════════════════
   FRAMES — one implementation of "a screen pushed inside a window" (N-6).

   The Workshop, the Project window and Settings each hand-rolled this walk:
   a LIFO of screens inside one window, stepped back one per Escape before
   the window itself closes. One hook now. Frames are the WINDOW'S OWN state
   — deliberately not entries on the app scene — so dismissing the window
   cascades its frames structurally: there is nowhere else they live.

     · `initial` seeds the walk, and re-seeds it when `deps` change — that is
       the deep-link rule: a link from outside replaces where you are, an
       ordinary re-render never does.
     · `accepts` refuses frames the window cannot draw (an older build's deep
       link would otherwise render an empty screen with no way back).
     · `replaceTop` is the created-thing walk: the making screen becomes the
       made thing's page, without deepening what Back has to unwind.
     · `ride` names the walk's registry row (settings-frame, workshop-frame,
       project-frame): every level above `ground` announces itself through
       the same mdsceneride door the confirm and the datepicker use, so one
       browser Back steps one frame out — Back = Escape (N-14). A walk with
       no ride is Escape-only, exactly as before. */
function useMdFrames(initial, deps = [initial], options = null) {
  const accepts = options && options.accepts ? options.accepts : null;
  const ride = options && options.ride ? options.ride : null;
  const ground = options && options.ground ? options.ground : 0;
  const ok = (frame) => !!frame && (!accepts || accepts(frame));
  const okRef = React.useRef(ok); okRef.current = ok;
  const [stack, setStack] = React.useState(() => (ok(initial) ? [initial] : []));
  const first = React.useRef(true);
  /* Layout effect, so a deep link's reseed lands before the frame paints —
     nobody sees the stale walk for a frame. */
  React.useLayoutEffect(() => {
    if (first.current) { first.current = false; return; }
    if (okRef.current(initial)) setStack([initial]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  const push = React.useCallback((next) => {
    if (okRef.current(next)) setStack((s) => [...s, next]);
  }, []);
  const pop = React.useCallback(() => setStack((s) => s.slice(0, -1)), []);
  const clear = React.useCallback(() => setStack([]), []);
  const replaceTop = React.useCallback((next) => {
    if (okRef.current(next)) setStack((s) => [...s.slice(0, -1), next]);
  }, []);
  /* The ride's tokens, one per seated level, reconciled against the stack
     after every walk move. A close from a journal walk pops the top frame —
     only the walk owner knows how to leave — and the level that left is
     released here on the next pass. Unmounting releases everything: the
     window's cascade reaches the journal too. */
  const popRef = React.useRef(pop); popRef.current = pop;
  const ridden = React.useRef([]);
  React.useEffect(() => {
    if (!ride) return;
    const want = Math.max(0, stack.length - ground);
    const have = ridden.current;
    while (have.length < want) {
      const token = { surface: ride, close: () => popRef.current() };
      have.push(token);
      window.dispatchEvent(new CustomEvent('mdsceneride', { detail: { token, open: true } }));
    }
    while (have.length > want) {
      const token = have.pop();
      window.dispatchEvent(new CustomEvent('mdsceneride', { detail: { token, open: false } }));
    }
  }, [ride, ground, stack.length]);
  React.useEffect(() => () => {
    for (const token of ridden.current.splice(0))
      window.dispatchEvent(new CustomEvent('mdsceneride', { detail: { token, open: false } }));
  }, []);
  return { stack, frame: stack.length ? stack[stack.length - 1] : null,
    push, pop, clear, replaceTop };
}

Object.assign(window, {
  mdDismissLayers,
  useMdDismissLayer,
  useMdFrames,
  /* The name half the app already calls it by. The alias stays until the
     sweep story retires it; both names are the same stack. */
  useDbEscapeLayer: useMdDismissLayer,
});
